import { authApiEndpoint } from "../utils/env";

/**
 * The identity directory — who exists in this organization.
 *
 * Lives against the AUTH service, not the REST API, because accounts are OATH's data: REST stores a user name
 * on a workflow step and never learns the person's name or e-mail. Anything that has to turn "a.yilmaz" into
 * "Ayşe Yılmaz" has to come here.
 *
 * Admin-gated server-side. A staff directory is a real disclosure — names, logins and e-mail addresses of
 * every colleague — so it is not offered to every authenticated user. Every screen that consumes it today is
 * already an admin screen; if a non-admin surface ever needs a picker, that needs its own narrower endpoint
 * rather than relaxing this one.
 */
async function fetchWithAuth(url, token, options = {}) {
  const headers = options.headers ? { ...options.headers } : {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  headers["Content-Type"] = headers["Content-Type"] || "application/json";
  const res = await fetch(url, { ...options, headers });
  const txt = await res.text();
  let json = null;
  try {
    json = txt ? JSON.parse(txt) : null;
  } catch {
    json = txt;
  }
  if (!res.ok) {
    const msg = json && json.message ? json.message : typeof json === "string" ? json : res.statusText;
    const error = new Error(msg || `Request failed: ${res.status}`);
    error.status = res.status;
    error.details = json;
    throw error;
  }
  return json;
}

/**
 * Search the organization's accounts by user name, full name or e-mail.
 *
 * @param token   bearer token
 * @param q       free text; blank returns the head of the directory rather than nothing, so a picker can be
 *                opened and browsed without typing
 * @param take    row cap (server clamps to 200)
 * @returns {Promise<{total:number, users:Array<{userName,fullName,email,organization}>}>}
 *          `total` is the match count BEFORE the cap, so the caller can say "showing 20 of 137" instead of
 *          implying the cap is the whole answer.
 */
export async function searchDirectory(token, q = "", take = 20) {
  const qs = `q=${encodeURIComponent(q || "")}&take=${encodeURIComponent(take)}`;
  return fetchWithAuth(authApiEndpoint(`/api/ApplicationUser/DirectorySearch?${qs}`), token, { method: "GET" });
}
